import * as THREE from 'three';
import { CommonClass } from './CommonClass';
import { MouseClass } from './MouseInteraction';
import { ShaderPass, Advection, ExternalForce, Viscous, Divergence, Poisson, Pressure } from './ShaderPasses';

export interface SimOptions {
  iterations_poisson: number;
  iterations_viscous: number;
  mouse_force: number;
  resolution: number;
  cursor_size: number;
  viscous: number;
  isBounce: boolean;
  dt: number;
  isViscous: boolean;
  BFECC: boolean;
}

export class Simulation {
  options: SimOptions;
  fbos: Record<string, THREE.WebGLRenderTarget | null> = {
    vel_0: null,
    vel_1: null,
    vel_viscous0: null,
    vel_viscous1: null,
    div: null,
    pressure_0: null,
    pressure_1: null
  };
  fboSize = new THREE.Vector2();
  cellScale = new THREE.Vector2();
  boundarySpace = new THREE.Vector2();
  advection!: Advection;
  externalForce!: ExternalForce;
  viscous!: Viscous;
  divergence!: Divergence;
  poisson!: Poisson;
  pressure!: Pressure;

  Common: CommonClass;
  Mouse: MouseClass;

  constructor(Common: CommonClass, Mouse: MouseClass, options?: Partial<SimOptions>) {
    this.Common = Common;
    this.Mouse = Mouse;
    this.options = {
      iterations_poisson: 32,
      iterations_viscous: 32,
      mouse_force: 20,
      resolution: 0.5,
      cursor_size: 100,
      viscous: 30,
      isBounce: false,
      dt: 0.014,
      isViscous: false,
      BFECC: true,
      ...options
    };
    this.init();
  }

  init() {
    this.calcSize();
    this.createAllFBO();
    this.createShaderPass();
  }

  getFloatType() {
    const isIOS = /(iPad|iPhone|iPod)/i.test(navigator.userAgent);
    return isIOS ? THREE.HalfFloatType : THREE.FloatType;
  }

  createAllFBO() {
    const type = this.getFloatType();
    const opts = {
      type,
      depthBuffer: false,
      stencilBuffer: false,
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping
    } as const;
    for (const key in this.fbos) {
      this.fbos[key] = new THREE.WebGLRenderTarget(this.fboSize.x, this.fboSize.y, opts);
    }
  }

  createShaderPass() {
    // Inject Common and Mouse into shader passes
    const injectionTarget = ShaderPass.prototype as any;
    injectionTarget.Common = this.Common;
    injectionTarget.Mouse = this.Mouse;

    this.advection = new Advection({
      cellScale: this.cellScale,
      fboSize: this.fboSize,
      dt: this.options.dt,
      src: this.fbos.vel_0,
      dst: this.fbos.vel_1
    });

    this.externalForce = new ExternalForce({
      cellScale: this.cellScale,
      cursor_size: this.options.cursor_size,
      dst: this.fbos.vel_1
    });

    this.viscous = new Viscous({
      cellScale: this.cellScale,
      boundarySpace: this.boundarySpace,
      viscous: this.options.viscous,
      src: this.fbos.vel_1,
      dst: this.fbos.vel_viscous1,
      dst_: this.fbos.vel_viscous0,
      dt: this.options.dt
    });

    this.divergence = new Divergence({
      cellScale: this.cellScale,
      boundarySpace: this.boundarySpace,
      src: this.fbos.vel_viscous0,
      dst: this.fbos.div,
      dt: this.options.dt
    });

    this.poisson = new Poisson({
      cellScale: this.cellScale,
      boundarySpace: this.boundarySpace,
      src: this.fbos.div,
      dst: this.fbos.pressure_1,
      dst_: this.fbos.pressure_0
    });

    this.pressure = new Pressure({
      cellScale: this.cellScale,
      boundarySpace: this.boundarySpace,
      src_p: this.fbos.pressure_0,
      src_v: this.fbos.vel_viscous0,
      dst: this.fbos.vel_0,
      dt: this.options.dt
    });
  }

  calcSize() {
    const width = Math.max(1, Math.round(this.options.resolution * this.Common.width));
    const height = Math.max(1, Math.round(this.options.resolution * this.Common.height));
    this.cellScale.set(1 / width, 1 / height);
    this.fboSize.set(width, height);
  }

  resize() {
    this.calcSize();
    for (const key in this.fbos) {
      this.fbos[key]!.setSize(this.fboSize.x, this.fboSize.y);
    }
  }

  update() {
    if (this.options.isBounce) this.boundarySpace.set(0, 0);
    else this.boundarySpace.copy(this.cellScale);

    this.advection.update({ dt: this.options.dt, isBounce: this.options.isBounce, BFECC: this.options.BFECC });
    this.externalForce.update({
      cursor_size: this.options.cursor_size,
      mouse_force: this.options.mouse_force,
      cellScale: this.cellScale
    });

    let vel: any = this.fbos.vel_1;
    if (this.options.isViscous) {
      vel = this.viscous.update({
        viscous: this.options.viscous,
        iterations: this.options.iterations_viscous,
        dt: this.options.dt
      });
    }

    this.divergence.update({ vel });
    const pressure = this.poisson.update({ iterations: this.options.iterations_poisson });
    this.pressure.update({ vel, pressure });
  }
}
